import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { AlertCircle, Coins, Loader2, UserCircle2 } from 'lucide-react';
import * as React from 'react';

/**
 * PermanentGrantDialog — STORY-1212 (B-13) partner-to-partner PERMANENT
 * AuthorizationGrant create dialog (ADR-124 D-1, Sally 03-partner §3.5).
 *
 * Scope (Phase 1):
 *  - granteePartnerId: UUID text input (Phase 2 follow-up → partner
 *    autocomplete search widget). Ez egy pragmatic gate-7 vágás: 5 SP
 *    scope-on belül a full-fledged Partner search komponens külön story
 *    (Q3 gate-7 decision 2026-04-24: 2 külön dialog PERMANENT/ONE_TIME).
 *  - validFrom (date), validUntil (optional date)
 *  - valueLimitHuf (optional integer HUF)
 *  - documentIds (optional — PERMANENT nem kötelező, ellentétben ONE_TIME-mal)
 *
 * Submit payload shape matches @kgc/core/auth-grant `CreatePermanentGrantDto`
 * fields; parent adds grantorPartnerId + createdBy + tenantId.
 *
 * In-flight guard + mount tracker mirror the OneTimeGrantDialog pattern
 * (B-12 review P2 #4).
 */

export interface PermanentGrantSubmitPayload {
  granteePartnerId: string;
  validFrom: string; // ISO date (YYYY-MM-DD)
  validUntil?: string | undefined; // ISO date
  valueLimitHuf?: number | undefined;
}

export interface PermanentGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Submit handler. Parent adds grantorPartnerId/createdBy/tenantId + API call. */
  onSubmit: (payload: PermanentGrantSubmitPayload) => Promise<void> | void;
}

// B-13 Blind P2 #1 — strict RFC4122 v4 UUID (version + variant bits enforced)
// AND reject the nil UUID (which is the field's own placeholder).
// Groups: 8-4-4-4-12 chars; group 3 starts with '4' (version), group 4 starts
// with [89ab] (variant 10xx). Fourth group is 4 chars total → [89ab][0-9a-f]{3}.
const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

// B-13 Edge P1 #4 — build local-date YYYY-MM-DD (not UTC-sliced toISOString).
// Budapest is UTC+1/+2; toISOString().slice(0,10) returns previous day in
// early-morning hours. Intl 'en-CA' formats local date as YYYY-MM-DD.
function localToday(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return parts;
}

export function PermanentGrantDialog({ open, onOpenChange, onSubmit }: PermanentGrantDialogProps) {
  const { t } = useTranslation();

  const [granteePartnerId, setGranteePartnerId] = React.useState('');
  const [validFrom, setValidFrom] = React.useState(() => localToday());
  const [validUntil, setValidUntil] = React.useState('');
  const [valueLimitHuf, setValueLimitHuf] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const inFlightRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset on open=true (fresh session) — mirrors OneTimeGrantDialog B-12
  // Blind Hunter P2 #3 fix (effect deps = [open] only).
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setGranteePartnerId('');
      setValidFrom(localToday());
      setValidUntil('');
      setValueLimitHuf('');
      setSubmitError(null);
    }
    prevOpenRef.current = open;
  }, [open]);

  const validate = React.useCallback((): string | null => {
    // B-13 Edge P1 #5 — normalize UUID case before validation + submit.
    const trimmedId = granteePartnerId.trim().toLowerCase();
    if (trimmedId.length === 0) {
      return t('authGrant.dialog.permanent.validationGranteeRequired');
    }
    // B-13 Blind P2 #1 — reject nil UUID (field's own placeholder) + non-v4.
    if (trimmedId === NIL_UUID || !UUID_V4_RE.test(trimmedId)) {
      return t('authGrant.dialog.permanent.validationGranteeUuid');
    }
    if (validFrom.length === 0) {
      return t('authGrant.dialog.permanent.validationValidFromRequired');
    }
    // B-13 Blind P2 #2 — block past-dated validFrom (audit-replay correctness).
    if (validFrom < localToday()) {
      return t('authGrant.dialog.permanent.validationValidFromPast');
    }
    // B-13 Edge P0 #1 — allow SAME-DAY grant (single-event use case);
    // reject only if validUntil is STRICTLY before validFrom.
    if (validUntil.length > 0 && validUntil < validFrom) {
      return t('authGrant.dialog.permanent.validationValidUntilAfter');
    }
    if (valueLimitHuf.length > 0) {
      const num = Number(valueLimitHuf);
      // B-13 Edge P1 #3 — require > 0 (0 HUF is a typo-trap, not "unlimited").
      // Empty field = no limit (passed as undefined).
      if (!Number.isFinite(num) || num < 1 || !Number.isInteger(num)) {
        return t('authGrant.dialog.permanent.validationValueLimit');
      }
    }
    return null;
  }, [granteePartnerId, validFrom, validUntil, valueLimitHuf, t]);

  const handleSubmit = React.useCallback(async () => {
    if (inFlightRef.current) return;
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    inFlightRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload: PermanentGrantSubmitPayload = {
        // B-13 Edge P1 #5 — normalize UUID to lowercase before API call.
        granteePartnerId: granteePartnerId.trim().toLowerCase(),
        validFrom,
        ...(validUntil.length > 0 ? { validUntil } : {}),
        ...(valueLimitHuf.length > 0 ? { valueLimitHuf: Number(valueLimitHuf) } : {}),
      };
      await onSubmit(payload);
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : String(ex);
      if (isMountedRef.current) setSubmitError(msg);
    } finally {
      inFlightRef.current = false;
      if (isMountedRef.current) setSubmitting(false);
    }
  }, [validate, granteePartnerId, validFrom, validUntil, valueLimitHuf, onSubmit]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (inFlightRef.current) return;
      onOpenChange(next);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="permanent-grant-dialog">
        <DialogHeader>
          <DialogTitle>{t('authGrant.dialog.permanent.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {submitError && (
            <div
              className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
              data-testid="permanent-grant-error"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{submitError}</span>
            </div>
          )}

          <div>
            <label htmlFor="permanent-grant-grantee" className="mb-1 block text-sm font-medium">
              {t('authGrant.dialog.permanent.granteeLabel')} *
            </label>
            <div className="relative">
              <UserCircle2 className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="permanent-grant-grantee"
                className="pl-8 font-mono text-xs"
                value={granteePartnerId}
                onChange={e => setGranteePartnerId(e.target.value)}
                disabled={submitting}
                placeholder="00000000-0000-0000-0000-000000000000"
                data-testid="permanent-grant-grantee"
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('authGrant.dialog.permanent.granteeHint')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="permanent-grant-valid-from"
                className="mb-1 block text-sm font-medium"
              >
                {t('authGrant.dialog.permanent.validFromLabel')} *
              </label>
              <Input
                id="permanent-grant-valid-from"
                type="date"
                value={validFrom}
                onChange={e => setValidFrom(e.target.value)}
                disabled={submitting}
                data-testid="permanent-grant-valid-from"
              />
            </div>
            <div>
              <label
                htmlFor="permanent-grant-valid-until"
                className="mb-1 block text-sm font-medium"
              >
                {t('authGrant.dialog.permanent.validUntilLabel')}
              </label>
              <Input
                id="permanent-grant-valid-until"
                type="date"
                value={validUntil}
                onChange={e => setValidUntil(e.target.value)}
                disabled={submitting}
                data-testid="permanent-grant-valid-until"
              />
            </div>
          </div>

          <div>
            <label htmlFor="permanent-grant-value-limit" className="mb-1 block text-sm font-medium">
              {t('authGrant.dialog.permanent.valueLimitLabel')}
            </label>
            <div className="relative">
              <Coins className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="permanent-grant-value-limit"
                type="number"
                inputMode="numeric"
                min={0}
                step={1000}
                className="pl-8"
                value={valueLimitHuf}
                onChange={e => setValueLimitHuf(e.target.value)}
                disabled={submitting}
                placeholder="pl. 500000"
                data-testid="permanent-grant-value-limit"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => handleOpenChange(false)}
            data-testid="permanent-grant-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={submitting}
            onClick={() => void handleSubmit()}
            data-testid="permanent-grant-submit"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {submitting ? t('common.saving') : t('authGrant.dialog.permanent.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
